import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/Button';
import { InputField } from '../../components/InputField';
import { SetupLayout } from '../../components/SetupLayout';
import { useSetup, SetupDeviceType } from '../../store/setupStore';
import { useAuth } from '../../store/authStore';
import { registerDevice } from '../../api/devices';
function BeltIllustration() {
  return <svg viewBox="0 0 200 200" className="w-full h-full" data-id="element-2110">
      <circle cx="100" cy="100" r="80" fill="#F3E8FF" data-id="element-2111" />
      <path d="M40 100 C40 60, 160 60, 160 100 C160 140, 40 140, 40 100 Z" fill="#6B21E8" opacity="0.8" data-id="element-2112" />
      <circle cx="70" cy="100" r="12" fill="#FFFFFF" data-id="element-2113" />
      <circle cx="100" cy="100" r="16" fill="#FFFFFF" data-id="element-2114" />
      <circle cx="130" cy="100" r="12" fill="#FFFFFF" data-id="element-2115" />
      <path d="M100 60 Q120 40 140 60" stroke="#00D4D4" strokeWidth="3" fill="none" strokeLinecap="round" className="animate-pulse" data-id="element-2116" />
      <path d="M100 140 Q120 160 140 140" stroke="#00D4D4" strokeWidth="3" fill="none" strokeLinecap="round" className="animate-pulse" data-id="element-2117" />
    </svg>;
}
function CradleIllustration() {
  return <svg viewBox="0 0 200 200" className="w-full h-full" data-id="element-2118">
      <circle cx="100" cy="100" r="80" fill="#F3E8FF" data-id="element-2119" />
      <path d="M50 100 Q100 70 150 100 L150 130 Q100 145 50 130 Z" fill="#6B21E8" opacity="0.85" data-id="element-2120" />
      <path d="M40 132 Q100 160 160 132" stroke="#6B21E8" strokeWidth="4" fill="none" strokeLinecap="round" data-id="element-2121" />
      <line x1="70" y1="90" x2="70" y2="105" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" data-id="element-2122" />
      <line x1="90" y1="84" x2="90" y2="105" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" data-id="element-2123" />
      <line x1="110" y1="84" x2="110" y2="105" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" data-id="element-2124" />
      <line x1="130" y1="90" x2="130" y2="105" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" data-id="element-2125" />
      <circle cx="100" cy="55" r="14" fill="#FFFFFF" data-id="element-2126" />
      <circle cx="100" cy="55" r="8" fill="#6B21E8" data-id="element-2127" />
      <circle cx="100" cy="55" r="3" fill="#00D4D4" data-id="element-2128" />
      <line x1="100" y1="69" x2="100" y2="78" stroke="#6B21E8" strokeWidth="3" strokeLinecap="round" data-id="element-2129" />
      <path d="M120 45 Q130 38 138 45" stroke="#00D4D4" strokeWidth="2.5" fill="none" strokeLinecap="round" className="animate-pulse" data-id="element-2130" />
      <path d="M125 38 Q135 28 145 38" stroke="#00D4D4" strokeWidth="2.5" fill="none" strokeLinecap="round" className="animate-pulse" data-id="element-2131" />
    </svg>;
}
export function DeviceEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    token
  } = useAuth();
  const {
    setDeviceType,
    deviceType,
    labels,
    setProvisioningData
  } = useSetup();
  const [uid, setUid] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const t = searchParams.get('type');
    if (t === 'cradle' || t === 'belt') {
      setDeviceType(t as SetupDeviceType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isCradle = deviceType === 'cradle';
  const handleLink = async () => {
    if (!uid.trim()) {
      setError('Please enter a Device UID');
      return;
    }
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const formattedUid = uid.trim().toUpperCase();
      const res = await registerDevice(token, formattedUid);
      setProvisioningData({
        deviceUid: formattedUid,
        deviceId: res.deviceId,
        provisioningToken: res.provisioningToken,
        expiresAt: res.expiresAt
      });
      navigate('/app/setup/waiting');
    } catch (err: any) {
      setError(err.message || `Couldn't link this ${labels.singular}. Please check the code and try again.`);
    } finally {
      setIsLoading(false);
    }
  };
  return <SetupLayout step={1} onBack={() => navigate('/app/profile/devices')} data-id="element-2132">
      <div className="flex flex-col items-center flex-1 justify-center" data-id="element-2133">
        <div className="w-full max-w-[160px] aspect-square mb-6 flex items-center justify-center" data-id="element-2134">
          {isCradle ? <CradleIllustration data-id="element-2135" /> : <BeltIllustration data-id="element-2136" />}
        </div>

        <h1 className="text-[26px] font-semibold text-text-primary text-center mb-3" data-id="element-2137">
          Link your {labels.singular}
        </h1>

        <p className="text-[16px] text-text-secondary text-center max-w-[320px] mb-8" data-id="element-2138">
          Enter the Device UID printed on the inside of your {labels.singular}'s
          box.
        </p>

        <div className="w-full max-w-[320px] space-y-6 mb-auto" data-id="element-2139">
          <div data-id="element-2140">
            <label htmlFor="deviceUid" className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-2141">
              Device UID
            </label>
            <InputField id="deviceUid" placeholder="e.g. ZGA9CZ" value={uid} onChange={e => {
            setUid(e.target.value.toUpperCase());
            if (error) setError(null);
          }} maxLength={12} autoComplete="off" data-id="element-2142" />
          </div>

          {error && <p className="text-[14px] text-danger text-center animate-fadeInUp" data-id="element-2143">
              {error}
            </p>}

          <Button onClick={handleLink} isLoading={isLoading} disabled={!uid.trim()} data-id="element-2144">
            Link {labels.titleCase}
          </Button>
        </div>
      </div>
    </SetupLayout>;
}